import { useEffect, useMemo, useState } from "react";
import { useTranslator } from "@eo-locale/react";
import { useRouter } from "next/router";
import toArray from "lodash/toArray";
import isEmpty from "lodash/isEmpty";
import truncate from "lodash/truncate";

import { getQuantifiableQuestions } from "@/helpers/survey";
import { fetchQuestionResult } from "@/redux/features/survey/thunk";
import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";
import {
  Box,
  Button,
  FiArrowLeft,
  FiArrowRight,
  FiChevronLeft,
  Flex,
  HorizontalBarChart,
  HTMLWrapper,
  Progress,
  Spacer,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Tooltip,
  useBreakpointValue
} from "@/storybook-components";
import { toggleResponse } from "@/redux/features/survey/slice";

const SurveyResults = () => {
  const { query } = useRouter();
  const dispatch = useAppDispatch();
  const { translate } = useTranslator();
  const survey = useAppSelector((state) => state.survey);
  const { results, options, answers, statements } = survey;
  const truncateLength = useBreakpointValue({
    base: 122,
    "2xl": 122,
    xl: 122,
    lg: 122,
    md: 38,
    sm: 38
  });

  const [allQuantifiedQuestions, setAllQuantifiedQuestions] = useState(
    useMemo(() => getQuantifiableQuestions(survey), [survey])
  );

  const { projectId, surveyId } = query;
  const currentQuestion = allQuantifiedQuestions.find(
    (question) => question.current
  );

  const showPreviousQuestion = () => {
    if (!currentQuestion.previous) return;

    setAllQuantifiedQuestions(
      allQuantifiedQuestions.map((question) => ({
        ...question,
        current: question.id === `${currentQuestion.previous}`
      }))
    );
  };

  const showNextQuestion = () => {
    if (!currentQuestion.next) return;

    setAllQuantifiedQuestions(
      allQuantifiedQuestions.map((question) => ({
        ...question,
        current: question.id === `${currentQuestion.next}`
      }))
    );
  };

  const progressNode = () => {
    const totalQuestionsLength = allQuantifiedQuestions.length;
    const currentQuestionIndex =
      allQuantifiedQuestions.findIndex((question) => question.current) + 1;

    const progress = (currentQuestionIndex / totalQuestionsLength) * 100;

    return (
      <Box width="100%">
        <Text
          mb={2}
          fontSize="sm"
          textAlign="center"
          data-testid="survey-participant-results-progress-label"
        >
          {[
            currentQuestionIndex,
            translate("common.of"),
            totalQuestionsLength,
            translate("common.questions")
          ].join(" ")}
        </Text>
        <Progress
          css={{
            "& [data-part=track]": { borderRadius: "4px", h: "3" },
            "& [data-part=range]": { borderRadius: 0 }
          }}
          mb={10}
          value={progress}
          data-testid="survey-participant-results-progress-bar"
        />
      </Box>
    );
  };

  useEffect(() => {
    if (!isEmpty(currentQuestion) && !results[currentQuestion.id]) {
      dispatch(
        fetchQuestionResult({
          projectId,
          surveyId,
          pageId: currentQuestion.pageId,
          questionId: currentQuestion.id
        })
      );
    }
  }, [currentQuestion, dispatch, projectId, results, surveyId]);

  const questionNode = () => {
    const { id, question, type } = currentQuestion;
    const sortedOptions = toArray(options)
      .filter((option) => id === option.questionId)
      .sort((a, b) => {
        // Match randomizeOptions in survey helpers: "Other" stays at the bottom
        if (Boolean(a.supportsTextAnswer) !== Boolean(b.supportsTextAnswer)) {
          return a.supportsTextAnswer ? 1 : -1;
        }
        return a.sequence - b.sequence;
      });

    const sortedStatements = toArray(statements)
      .filter((ls) => ls.parentId === parseInt(id))
      .sort((a, b) => (a.sequence > b.sequence ? 1 : -1));

    const isOptionAnswered = (option, answer) => {
      if (type === "RankingQuestion" && results[id]) {
        return (
          results[id][option.id] === Math.max(...Object.values(results[id]))
        );
      }

      if (Array.isArray(answer?.id)) {
        return answer.id.indexOf(parseInt(option.id, 10)) > -1;
      }

      if (type === "LikertQuestion") return answer === parseInt(option.id, 10);

      return answer?.id === parseInt(option.id, 10);
    };

    if (type === "RankingQuestion") {
      const answer = answers[id] ?? { id: [] };

      return (
        <Box>
          <Text size="md" fontWeight={600}>
            <HTMLWrapper html={question} />
          </Text>
          <Box my={6}>
            <TableContainer>
              <Table
                striped
                colorPalette="gray"
                css={{
                  "& tbody tr:nth-of-type(odd) td": { bg: "gray.100" },
                  // v2's striped variant coloured th/td borders `gray.100`, the
                  // same token as the striped row bg, so borders faded into the
                  // stripes and read as borderless on gray colorScheme. v3 no
                  // longer has a striped variant — `striped` is a boolean and
                  // the recipe falls back to the `line` variant, which draws a
                  // themed border token on every row. Restate v2's colour to
                  // hide the borders again.
                  "& th, & td": { borderColor: "gray.100" }
                }}
              >
                <Thead>
                  <Tr>
                    <Th textAlign="center">{translate("survey.option")}</Th>
                    <Th textAlign="center">{translate("survey.average")}</Th>
                    <Th textAlign="center">{translate("survey.yours")}</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {sortedOptions.map((option) => (
                    <Tr key={option.id}>
                      <Td
                        height="46px"
                        maxW={{
                          sm: "136px",
                          md: "183px",
                          lg: "372px",
                          xl: "314px",
                          "2xl": "474px"
                        }}
                        textAlign="end"
                        whiteSpace="initial"
                      >
                        <Tooltip
                          label={option.name}
                          aria-label={option.name}
                          disabled={option.name.length < truncateLength}
                        >
                          {truncate(option.name, { length: truncateLength })}
                        </Tooltip>
                      </Td>
                      <Td
                        maxW={{
                          sm: "76px",
                          md: "99.5px",
                          lg: "174px",
                          xl: "145px",
                          "2xl": "225px"
                        }}
                        textAlign="center"
                      >
                        {isEmpty(results[id]) ? 0 : results[id][option.id]}
                      </Td>
                      <Td
                        maxW={{
                          sm: "76px",
                          md: "99.5px",
                          lg: "174px",
                          xl: "145px",
                          "2xl": "225px"
                        }}
                        textAlign="center"
                      >
                        {answer?.id.includes(option.id) && !isEmpty(results[id])
                          ? answer?.id.indexOf(option.id) + 1
                          : 0}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      );
    }

    if (type === "LikertQuestion") {
      const answer = answers[id] || {};

      return (
        <Box>
          <Text size="md" fontWeight={600}>
            <HTMLWrapper html={question} />
          </Text>
          <Box my={6}>
            {sortedStatements.map((statement) => {
              const data = sortedOptions.map((option) => ({
                name: option.name,
                highlight: isOptionAnswered(option, answer[statement.id]),
                value: isEmpty(results[id])
                  ? 0
                  : results[id][statement.id][option.id],
                legend: isOptionAnswered(option, answer[statement.id])
                  ? translate("survey.yourSelection")
                  : translate("survey.otherOptions")
              }));

              return (
                <Box key={statement.id}>
                  <HTMLWrapper html={statement.question} />
                  <Box my={6}>
                    <HorizontalBarChart data={data} />
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      );
    }

    const answer = answers[id] ?? { id: [] };
    // Stored sequence stays negative to positive. Reverse only the chart.
    const displayOptions =
      type === "EmojiQuestion" && currentQuestion.reverseOptions
        ? [...sortedOptions].reverse()
        : sortedOptions;
    const data = displayOptions.map((option) => {
      return {
        name: option.name,
        value: isEmpty(results[id]) ? 0 : results[id][option.id],
        highlight: isOptionAnswered(option, answer),
        legend: isOptionAnswered(option, answer)
          ? type === "RankingQuestion"
            ? translate("survey.topRanked")
            : translate("survey.yourSelection")
          : translate("survey.otherOptions")
      };
    });

    return (
      <Box>
        <Text size="md" fontWeight={600}>
          <HTMLWrapper html={question} />
        </Text>
        <Box my={6}>
          <HorizontalBarChart data={data} />
        </Box>
      </Box>
    );
  };

  if (isEmpty(currentQuestion)) return null;

  return (
    <>
      <Box data-testid="survey-participant-results">
        {progressNode()}
        {questionNode()}
      </Box>
      <Box>
        <Flex>
          {currentQuestion.previous && (
            <Button
              variant="ghost"
              leftIcon={<FiArrowLeft />}
              onClick={showPreviousQuestion}
              data-testid="survey-participant-results-button-previous-question"
            >
              {translate("common.previous")}
            </Button>
          )}
          <Spacer />
          {currentQuestion.next && (
            <Button
              variant="ghost"
              rightIcon={<FiArrowRight />}
              onClick={showNextQuestion}
              data-testid="survey-participant-results-button-next-question"
            >
              {translate("common.next")}
            </Button>
          )}
        </Flex>
        <Button
          mt={6}
          variant="ghost"
          leftIcon={<FiChevronLeft />}
          onClick={() => dispatch(toggleResponse())}
          data-testid="survey-participant-results-button-back-to-thanks"
        >
          {translate("survey.backToSurvey")}
        </Button>
      </Box>
    </>
  );
};

export default SurveyResults;
